import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Circle, CheckCircle2, X } from 'lucide-react'
import { useMarkReady, useUnmarkReady, useRemoveParty } from '../../hooks/useNegotiations'
import { ConfirmModal } from '../common/ConfirmModal'
import type { NegotiationParty } from '../../services/negotiationsApi'

interface ReadyStatusProps {
  negotiationId: number
  parties: NegotiationParty[]
  actorId?: string
}

export function ReadyStatus({ negotiationId, parties, actorId }: ReadyStatusProps) {
  const { t } = useTranslation()
  const markReady = useMarkReady()
  const unmarkReady = useUnmarkReady()
  const removePartyMut = useRemoveParty()
  const [partyToRemove, setPartyToRemove] = useState<NegotiationParty | null>(null)

  const activeParties = parties.filter((p) => p.is_active)
  const readyCount = activeParties.filter((p) => p.is_ready_to_sign).length
  const totalCount = activeParties.length
  const allReady = readyCount === totalCount && totalCount > 0

  // Find current actor's party
  const myParty = activeParties.find((p) => p.actor?.koblink_id === actorId)
  const isMyReady = myParty?.is_ready_to_sign ?? false
  const isOwner = myParty?.role === 'OWNER' || myParty?.role === 'TEAM'

  const handleToggle = () => {
    if (isMyReady) {
      unmarkReady.mutate({ negotiationId })
    } else {
      markReady.mutate({ negotiationId })
    }
  }

  return (
    <div className="px-3 py-2.5 border-b border-neutral-100 dark:border-neutral-800">
      {/* Header with count */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <CheckCircle2 size={12} className={allReady ? 'text-success-500' : 'text-neutral-400'} />
        <span className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">
          {t('negotiate.readyToSign')}
        </span>
        <span className="text-[10px] text-neutral-400">
          {t('negotiate.readyCount', { ready: readyCount, total: totalCount })}
        </span>
      </div>

      {/* All ready banner */}
      {allReady && (
        <div className="mb-1.5 px-2 py-1 rounded bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800">
          <p className="text-[10px] text-success-600 dark:text-success-400 font-medium">
            {t('negotiate.allPartiesReady')}
          </p>
        </div>
      )}

      {/* Party status list */}
      <div className="space-y-0.5">
        {activeParties.map((party) => {
          const isMe = party.actor?.koblink_id === actorId
          const canRemove = isOwner && !isMe && party.role !== 'OWNER'
          return (
            <div key={party.id} className="group flex items-center gap-1.5">
              {party.is_ready_to_sign ? (
                <Check size={10} className="text-success-500 shrink-0" />
              ) : (
                <Circle size={10} className="text-neutral-300 dark:text-neutral-600 shrink-0" />
              )}
              <span
                className="flex-1 text-[10px] truncate"
                style={{ color: party.color }}
              >
                {party.party_label}
                {isMe && ' (you)'}
              </span>
              {canRemove && (
                <button
                  onClick={() => setPartyToRemove(party)}
                  className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-500 transition-all"
                  title={t('negotiate.removeParty')}
                >
                  <X size={10} />
                </button>
              )}
            </div>
          )
        })}
      </div>

      {/* Confirm remove party modal */}
      <ConfirmModal
        open={!!partyToRemove}
        title={t('negotiate.removeParty')}
        description={t('negotiate.removePartyConfirm', {
          name: partyToRemove?.party_label,
          defaultValue: 'Remove {{name}} from this negotiation? Their comments will be preserved.',
        })}
        confirmLabel={t('negotiate.removeParty')}
        variant="danger"
        isPending={removePartyMut.isPending}
        onConfirm={() => {
          if (partyToRemove) {
            removePartyMut.mutate(
              { negotiationId, partyId: partyToRemove.id },
              { onSuccess: () => setPartyToRemove(null) },
            )
          }
        }}
        onCancel={() => setPartyToRemove(null)}
      />

      {/* Toggle button for current actor */}
      {myParty && (
        <button
          onClick={handleToggle}
          disabled={markReady.isPending || unmarkReady.isPending}
          className={`mt-2 w-full flex items-center justify-center gap-1 px-2 py-1.5 text-[11px] font-medium rounded transition-all duration-200 hover:shadow-md disabled:opacity-50 ${
            isMyReady
              ? 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:hover:bg-neutral-700'
              : 'bg-success-50 text-success-700 hover:bg-success-100 dark:bg-success-900/20 dark:text-success-400 dark:hover:bg-success-900/30'
          }`}
        >
          {isMyReady ? (
            <>
              <Circle size={10} />
              {t('negotiate.unmarkReady')}
            </>
          ) : (
            <>
              <Check size={10} />
              {t('negotiate.markReady')}
            </>
          )}
        </button>
      )}
    </div>
  )
}
